import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from '@nimbitlabs/ui';

interface HeroSectionProps {
  appName?: string;
  tagline?: string;
  description?: string;
}

const HeroSection = ({ 
  appName = "Hydranaut",
  tagline = "Service Catalog & Platform Health Monitoring",
  description = "Setup in minutes. Onboard new team members fast. Monitor service health in real-time. Visualize dependencies instantly. Get alerts that actually help. Complete visibility into your services, teams, and infrastructure."
}: HeroSectionProps) => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center pt-16 md:pt-20">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-0">

          {/* Left side: Text */}
          <div className="flex-1 text-center lg:text-left">
            {/* Main Heading */}
            <h1
              className="text-4xl md:text-5xl lg:text-7xl font-bold font-space mb-4 md:mb-6 leading-tight opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}
            >
              Master Your{" "}
              <span className="text-foreground">Service Ecosystem</span>
            </h1>

            {/* Subtitle */}
            <p
              className="text-base md:text-xl text-muted-foreground mb-6 md:mb-10 leading-relaxed opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              {description}
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mb-8 md:mb-16 opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.6s" }}
            >
              <Button variant="default" size="xl" asChild>
                <Link to="/signup">
                  Sign up Today
                  <ArrowRight className="w-5 h-5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right side: Image */}
          <div className="flex-shrink-0">
            <img
              src="/octopus.png"
              alt="Octopus"
              className="max-w-md h-auto opacity-0 animate-fade-in-up"
              style={{ animationDelay: "1s" }}
            />
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/3 left-10 w-3 h-3 rounded-full bg-primary/60" />
      <div className="absolute top-1/2 right-16 w-2 h-2 rounded-full bg-accent/60" style={{ animationDelay: "-2s" }} />
      <div className="absolute bottom-1/3 left-1/4 w-4 h-4 rounded-full bg-cyan-accent/40" style={{ animationDelay: "-4s" }} />
    </section>
  );
};


export default HeroSection;
